import type { Id } from "@convex/_generated/dataModel";
import { useMutation } from "@tanstack/react-query";
import { useAction } from "convex/react";
import { Check, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  type LocationById,
  posTerminalLocationsListTerminalLocations,
  posTerminalLocationsSetLocationTerminalLocation,
} from "~/api-refs";
import { Button } from "~/components/ui/button";
import { convexClient } from "~/integrations/convex/provider";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

interface TerminalLocationSelectorProps {
  location: LocationById;
}

interface TerminalLocation {
  id: string;
  displayName: string;
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

export const TerminalLocationSelector = ({
  location,
}: TerminalLocationSelectorProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState<TerminalLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<
    string | undefined
  >(location.stripeTerminalLocationId);

  useEffect(() => {
    setSelectedLocationId(location.stripeTerminalLocationId);
  }, [location.stripeTerminalLocationId]);

  const listTerminalLocations = useAction(
    posTerminalLocationsListTerminalLocations
  );
  const setLocationTerminalLocationMutation = useMutation({
    mutationFn: (data: {
      locationId: Id<"locations">;
      stripeTerminalLocationId: string;
    }) =>
      convexClient.mutation(
        posTerminalLocationsSetLocationTerminalLocation,
        data
      ),
  });

  const selectedLocation = useMemo(() => {
    if (!selectedLocationId) {
      return null;
    }

    return locations.find((terminalLocation) => {
      return terminalLocation.id === selectedLocationId;
    });
  }, [locations, selectedLocationId]);

  const handleFetchLocations = async () => {
    setIsLoading(true);
    try {
      const result = await listTerminalLocations({});
      if (result.success) {
        setLocations(result.locations);
        if (result.locations.length === 0) {
          toast.info("No Terminal Locations found", {
            description:
              "Please create a Terminal Location in your Stripe Dashboard first.",
          });
        }
      }
    } catch (error) {
      logger.exception("terminal_locations_fetch_failed", error);
      toast.error("Failed to fetch Terminal Locations", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectLocation = async (terminalLocationId: string) => {
    if (
      setLocationTerminalLocationMutation.isPending ||
      terminalLocationId === selectedLocationId
    ) {
      return;
    }

    try {
      await setLocationTerminalLocationMutation.mutateAsync({
        locationId: location._id,
        stripeTerminalLocationId: terminalLocationId,
      });
      setSelectedLocationId(terminalLocationId);
      ProductEvents.terminalSetup(location._id);
      toast.success("Terminal Location updated", {
        description:
          "POS devices at this location will use the selected Stripe Terminal Location.",
      });
    } catch (error) {
      logger.exception("terminal_location_set_failed", error);
      toast.error("Failed to update Terminal Location", {
        description: "Please try again.",
      });
    }
  };

  return (
    <div className="mt-6 space-y-2">
      <div className="flex items-center justify-between">
        <p className="label text-foreground leading-tight">
          Stripe Terminal Location
        </p>
        <Button
          disabled={isLoading || setLocationTerminalLocationMutation.isPending}
          onClick={handleFetchLocations}
          size="sm"
          type="button"
        >
          {(() => {
            if (isLoading) {
              return "Loading...";
            }
            if (locations.length > 0) {
              return "Refresh";
            }
            return "Load Locations";
          })()}
        </Button>
      </div>

      <div className="space-y-1">
        <p className="body-sm text-foreground">
          Select the Stripe Terminal Location for POS devices at this location.
        </p>
        {selectedLocationId ? (
          <p className="body-sm text-muted-foreground">
            Current selection:{" "}
            {selectedLocation?.displayName ?? selectedLocationId}
          </p>
        ) : (
          <p className="body-sm text-muted-foreground">
            No Stripe Terminal Location selected yet.
          </p>
        )}
      </div>

      {locations.length > 0 ? (
        <div className="mt-2 space-y-2">
          {locations.map((terminalLocation) => (
            <button
              className={`w-full rounded-lg border p-4 text-left transition-[color,box-shadow] ${
                selectedLocationId === terminalLocation.id
                  ? "border-primary bg-primary/10"
                  : "border-border bg-background hover:border-border/80"
              }`}
              disabled={setLocationTerminalLocationMutation.isPending}
              key={terminalLocation.id}
              onClick={() => handleSelectLocation(terminalLocation.id)}
              type="button"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-foreground text-weight-medium">
                    {terminalLocation.displayName}
                  </p>
                  <p className="body-sm text-foreground">
                    {terminalLocation.address.line1}
                    {terminalLocation.address.line2
                      ? `, ${terminalLocation.address.line2}`
                      : ""}
                  </p>
                  <p className="body-sm text-foreground">
                    {terminalLocation.address.city},{" "}
                    {terminalLocation.address.state}{" "}
                    {terminalLocation.address.postalCode}
                  </p>
                </div>
                {selectedLocationId === terminalLocation.id ? (
                  <div className="flex items-center space-x-2">
                    {setLocationTerminalLocationMutation.isPending ? (
                      <Loader2 className="text-primary size-5 animate-spin" />
                    ) : (
                      <Check
                        aria-hidden="true"
                        className="text-primary size-5"
                      />
                    )}
                  </div>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="border-border rounded-lg border border-dashed px-4 py-3">
          <p className="body-sm text-muted-foreground">
            Load Stripe Terminal Locations to choose one for this location.
          </p>
        </div>
      )}
    </div>
  );
};
